/**
 * Pure reconciliation model for the server-synced family cart.
 *
 * The Zustand store applies every mutation optimistically and records the
 * touched lines in `pendingByKey` (quantity <= 0 entries are tombstones —
 * the full line is kept so a deletion can still be serialized to the wire
 * after it left `linesByLocation`). Server hydration then REPLACES local
 * state except for those dirty keys, and a successful flush settles only
 * the keys whose value didn't change mid-flight. No React, no `@/api`
 * runtime imports — unit-testable exactly like `cart.ts`.
 */

import { type FamilyCartLine, upsertLine } from './cart'

/** Locally-edited, not-yet-flushed lines. `quantity <= 0` = delete tombstone. */
export type PendingByKey = Record<string, FamilyCartLine>

export function hasPendingLines(pending: PendingByKey): boolean {
  return Object.keys(pending).length > 0
}

/**
 * Replace-with-overlay hydration: bucket the server lines by campus, then
 * re-apply every pending local edit on top (an edit wins over the server
 * for its key; a tombstone drops the server line). Buckets come back
 * key-sorted so repeated hydrations of identical state are structurally
 * equal (see `linesByLocationEqual`).
 */
export function mergeServerLines(server: FamilyCartLine[], pending: PendingByKey): Record<string, FamilyCartLine[]> {
  const buckets: Record<string, FamilyCartLine[]> = {}
  for (const line of server) {
    if (line.quantity <= 0) {
      continue
    }
    ;(buckets[line.locationId] ??= []).push(line)
  }
  for (const line of Object.values(pending)) {
    buckets[line.locationId] = upsertLine(buckets[line.locationId] ?? [], line)
  }
  for (const [locationId, lines] of Object.entries(buckets)) {
    if (lines.length === 0) {
      delete buckets[locationId]
    } else {
      buckets[locationId] = [...lines].sort((a, b) => a.key.localeCompare(b.key))
    }
  }
  return buckets
}

/**
 * Settle a completed flush: drop a pending key only when its CURRENT value
 * still equals what was flushed — an edit that landed while the request
 * was in flight stays pending for the next round.
 */
export function settleFlushed(pending: PendingByKey, flushed: PendingByKey): PendingByKey {
  const next: PendingByKey = {}
  for (const [key, line] of Object.entries(pending)) {
    const sent = flushed[key]
    if (!sent || sent.quantity !== line.quantity || sent.unitPriceCents !== line.unitPriceCents) {
      next[key] = line
    }
  }
  return next
}

/**
 * Structural equality of two `linesByLocation` shapes — lets hydration
 * skip the `set()` (and the re-render fan-out) when a refetch returned
 * the state we already hold. Assumes both sides bucket-sort by key
 * (`mergeServerLines` guarantees it for hydrated state).
 */
export function linesByLocationEqual(
  a: Record<string, FamilyCartLine[]>,
  b: Record<string, FamilyCartLine[]>,
): boolean {
  const aKeys = Object.keys(a)
  if (aKeys.length !== Object.keys(b).length) {
    return false
  }
  for (const locationId of aKeys) {
    const aLines = a[locationId]
    const bLines = b[locationId]
    if (!aLines || !bLines || aLines.length !== bLines.length) {
      return false
    }
    for (let i = 0; i < aLines.length; i++) {
      if (!cartLinesEqual(aLines[i], bLines[i])) {
        return false
      }
    }
  }
  return true
}

function cartLinesEqual(a: FamilyCartLine | undefined, b: FamilyCartLine | undefined): boolean {
  if (!a || !b) {
    return a === b
  }
  return (
    a.key === b.key &&
    a.locationId === b.locationId &&
    a.organizationId === b.organizationId &&
    a.date === b.date &&
    a.mealtime === b.mealtime &&
    a.studentMemberId === b.studentMemberId &&
    a.studentUserId === b.studentUserId &&
    a.gradeRangeId === b.gradeRangeId &&
    a.menuItemId === b.menuItemId &&
    a.name === b.name &&
    a.imageUri === b.imageUri &&
    a.unitPriceCents === b.unitPriceCents &&
    a.currency === b.currency &&
    a.quantity === b.quantity &&
    (a.specialDietAccommodationId ?? null) === (b.specialDietAccommodationId ?? null)
  )
}

/**
 * Parse the pre-sync cart out of the Zustand `persist` envelope
 * (`{ state: { linesByLocation }, version }`) that older builds wrote to
 * `sessionStorage['ordo.family-cart']`. Defensive: bad JSON / foreign
 * shapes → `[]`.
 */
export function readLegacySessionCart(raw: string | null): FamilyCartLine[] {
  if (!raw) {
    return []
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return []
  }
  const state = (parsed as { state?: unknown }).state
  if (typeof state !== 'object' || state === null) {
    return []
  }
  const buckets = (state as { linesByLocation?: unknown }).linesByLocation
  if (typeof buckets !== 'object' || buckets === null) {
    return []
  }
  const lines: FamilyCartLine[] = []
  for (const value of Object.values(buckets)) {
    if (!Array.isArray(value)) {
      continue
    }
    for (const candidate of value) {
      if (isPlausibleLegacyLine(candidate)) {
        lines.push(candidate)
      }
    }
  }
  return lines
}

function isPlausibleLegacyLine(value: unknown): value is FamilyCartLine {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const line = value as Record<string, unknown>
  const requiredStrings = [
    'key',
    'locationId',
    'organizationId',
    'date',
    'mealtime',
    'studentMemberId',
    'studentUserId',
    'menuItemId',
    'name',
  ]
  if (requiredStrings.some((field) => typeof line[field] !== 'string' || (line[field] as string).length === 0)) {
    return false
  }
  return typeof line['quantity'] === 'number' && Number.isFinite(line['quantity'])
}

/**
 * One-time sessionStorage → server migration plan, SERVER-WINS: only
 * legacy lines whose key exists neither on the server nor in the pending
 * overlay survive (no quantity summing — a second tab racing the same
 * migration must not double the cart).
 */
export function planLegacyMigration(
  legacy: FamilyCartLine[],
  serverLines: FamilyCartLine[],
  pending: PendingByKey,
): FamilyCartLine[] {
  const taken = new Set<string>(serverLines.map((line) => line.key))
  for (const key of Object.keys(pending)) {
    taken.add(key)
  }
  return legacy.filter((line) => line.quantity > 0 && !taken.has(line.key))
}
